import dayjs from "dayjs"
import { useAppSelector } from "../../app/hooks"
import Table from "../../components/Table/Table"
import { selectInvoices } from "../../slices/invoicesSlice"
import { GridCellParams, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { formatPriceWithRupeeSymbol } from "../../utils/price-formatter"
import { useNavigate } from "react-router-dom"
import { USERS } from "../../constants/apis"
import { IconButton } from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"

interface InvoiceTableProps {
  rowCount: number
  paginationModel: any
  onPaginationModelChange: (model: any) => void
  isSmallScreen: boolean
}

const InvoiceTable = ({
  rowCount,
  paginationModel,
  onPaginationModelChange,
}: InvoiceTableProps) => {
  const { invoices, loading } = useAppSelector(selectInvoices)
  const navigate = useNavigate()

  const columns: GridColDef[] = [
    { field: "companyName", headerName: "Company Name", width: 300 },
    { field: "centerName", headerName: "Center", width: 300 },
    {
      field: "user",
      headerName: "User",
      width: 150,
    },
    { field: "email", headerName: "Email", width: 250 },
    { field: "mobile", headerName: "Mobile", width: 150 },
    { field: "bookingCode", headerName: "Booking Code", width: 150 },
    { field: "invoiceNumber", headerName: "Invoice Number", width: 150 },
    { field: "invoiceDate", headerName: "Invoice Date", width: 150 },
    { field: "invoiceDueDate", headerName: "Invoice Due Date", width: 150 },
    { field: "amount", headerName: "Amount", width: 150 },
    { field: "gstNumber", headerName: "GST Number", width: 150 },
    {
      field: "gstResolved",
      headerName: "GST Resolved",
      width: 150,
      type: "boolean",
    },
    {
      field: "invoiceUrl",
      headerName: "Download Invoice",
      width: 150,
      renderCell: ({ row }) =>
        row?.invoiceUrl && (
          <IconButton
            sx={{ mr: "4px" }}
            onClick={(e) => {
              e.stopPropagation()
              window.open(row.invoice_url)
            }}
          >
            <DownloadIcon color="primary" />
          </IconButton>
        ),
    },
  ]

  const rows: GridRowsProp = invoices?.map((invoice) => ({
    id: invoice?.id,
    companyName: invoice?.gstContact?.contactInfo?.companyName,
    user:
      invoice?.userDetails?.firstName + " " + invoice?.userDetails?.lastName,
    userId: invoice?.userDetails?.id,
    email: invoice?.userDetails?.email,
    mobile: invoice?.userDetails?.mobile,
    invoiceNumber: invoice?.invoiceNumber,
    centerName: invoice?.center?.name,
    invoiceDate: dayjs(invoice?.invoiceData?.date).format("DD/MM/YYYY"),
    invoiceDueDate: dayjs(invoice?.invoiceData?.dueDate).format("DD/MM/YYYY"),
    amount: formatPriceWithRupeeSymbol(
      invoice.invoiceData.lineItems.reduce(
        (sum, item) => sum + item.itemTotal,
        0,
      ),
    ),
    gstNumber: invoice.invoiceData.gstNo,
    gstResolved: invoice?.order?.isGstResolved,
    invoiceUrl: invoice?.invoiceUrl,
    bookingCode: invoice?.bookindCode,
  }))

  const handleRowClick = (params: GridCellParams) => {
    if (params.field === "user")
      navigate(`${USERS}/${params.row.userId}/profile`, {
        state: {
          title: "User Details",
          prevPage: "Users",
          path: location.pathname,
        },
      })
  }

  return (
    <Table
      rows={rows}
      columns={columns}
      loading={loading}
      rowCount={rowCount}
      pageSizeOptions={[10, 25, 50]}
      paginationModel={paginationModel}
      paginationMode="server"
      onPaginationModelChange={onPaginationModelChange}
      onCellClick={handleRowClick}
    />
  )
}

export default InvoiceTable
